import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../Atoms/Modal/Modal';
import ConfirmDialog from './ConfirmDialog';
import styles from './IconPicker.module.scss';
import { WEBSITE_URL } from '../../../Constants';
import iconCatalogue from '../../../config/websiteIcons.json';
import {
  useIcons,
  useCreateIcon,
  useDeleteIcon,
  iconLibraryUrl,
} from '../../../Hooks/Icons';

/**
 * Pick an icon — from the CMS icon library, or from the website's own icon set.
 *
 * TWO SOURCES, ONE GALLERY
 * ------------------------
 * 1. THE LIBRARY (`/api/Icons`) — icons an operator uploaded here. Listed per
 *    module (`?ModuleKey=…`), addable and removable from inside this dialog.
 *    This is the only half anyone in the CMS can change.
 * 2. THE WEBSITE CATALOGUE (`config/websiteIcons.json`) — a generated inventory
 *    of the files DPA-FE ships in `public/`, written by
 *    `scripts/generate-website-icons.mjs`. Read-only here: an operator cannot
 *    add to or delete from the website's own build output, so the delete
 *    control is ABSENT on those tiles rather than present and disabled.
 *
 * They are drawn as one gallery because the operator's question is "which
 * picture", not "which store". The library comes FIRST — it is the shorter,
 * deliberately curated list, and it is the one they have just uploaded into.
 *
 * WHAT IS STORED
 * --------------
 * A catalogue pick stores a ROOT-RELATIVE PATH
 * (`/assets/svg/header/search.svg`) which the WEBSITE resolves against its own
 * origin; the absolute preview URL built here never reaches the API. A library
 * pick stores the ABSOLUTE blob URL — see `iconLibraryUrl` in `Hooks/Icons` for
 * why the two differ. Downstream, both are just `iconUrl` strings.
 *
 * THE UPLOAD USED TO BE A BORROWED ENDPOINT
 * -----------------------------------------
 * Until the `Icons` controller existed this dialog POSTed to
 * `Degree/UploadFileToBlob` and then guessed at the URL in an undocumented
 * reply (`useUploadIcon` / `extractUploadedUrl`, still in
 * `Hooks/NavigationMenu` for anything else that needs them). That route dropped
 * the file into a blob store and remembered nothing: there was no list, so an
 * icon uploaded last week was unfindable this week, and no delete, so a mistake
 * was permanent. `/api/Icons` is the real thing — do not reach for the old pair
 * here again.
 */

/** Anything an <img> can draw, kept in step with the generator's EXTENSIONS. */
const ACCEPT = 'image/svg+xml,image/png,image/webp,image/jpeg,image/gif';

/**
 * 512 KB. An icon is a glyph; anything larger is a photograph that has wandered
 * into the wrong field, and it would be loaded on every page of the website.
 */
const MAX_BYTES = 512 * 1024;

/**
 * Hoisted out of the component: the catalogue is a static import, so reading
 * `iconCatalogue.groups ?? []` per render would hand every dependent useMemo a
 * brand-new array and re-filter 387 icons on every keystroke.
 */
const GROUPS = iconCatalogue.groups ?? [];

/**
 * The library's pseudo-group key in the source <select>.
 *
 * Double-underscored so it can never collide with a real catalogue key, which
 * the generator takes from directory names.
 */
const LIBRARY_KEY = '__library__';

/**
 * Which module's icons this picker manages. The navigation menu is the only
 * caller today; anything else that mounts the picker should pass its own key so
 * its uploads stay out of this gallery.
 */
const DEFAULT_MODULE_KEY = 'NavigationMenu';

const isAbsolute = (value) => /^(https?:)?\/\//i.test(value) || /^data:/i.test(value);

/** The preview address. Never stored — see the header note. */
export const iconPreviewSrc = (path) => {
  const value = (path ?? '').trim();
  if (!value) return '';
  if (isAbsolute(value)) return value;
  return `${WEBSITE_URL}${value.startsWith('/') ? '' : '/'}${value}`;
};

const formatBytes = (bytes) =>
  bytes >= 1024 ? `${Math.round(bytes / 1024)} ك.ب` : `${bytes} بايت`;

/** `logo-white.svg` -> `logo-white`. The operator can still overwrite it. */
const fileBaseName = (fileName) =>
  (fileName ?? '').replace(/\.[^./\\]+$/, '').trim() || 'أيقونة';

/* -------------------------------------------------------------------------- */

/**
 * One gallery cell.
 *
 * The delete control is a SIBLING of the select button, not a child of it: a
 * <button> inside a <button> is invalid HTML and browsers resolve it by
 * discarding the inner one — which would make the delete silently unclickable
 * on exactly the tiles that need it.
 */
const IconTile = ({ icon, isSelected, onSelect, onDelete }) => {
  // A tile whose file 404s renders as a labelled gap rather than the browser's
  // broken-image glyph: the catalogue can outlive a file the website deleted,
  // and "this one is missing" is the useful message.
  const [isBroken, setIsBroken] = useState(false);

  return (
    <div className={styles['tile-shell']}>
      <button
        type='button'
        className={`${styles.tile} ${isSelected ? styles['tile-selected'] : ''}`}
        onClick={() => onSelect(icon.path)}
        title={icon.path}
        aria-pressed={isSelected}
      >
        {isSelected && (
          <span className={styles['tile-check']} aria-hidden='true'>
            ✓
          </span>
        )}
        <span className={styles['tile-art']}>
          {isBroken ? (
            <span className={styles['tile-broken']} aria-hidden='true'>
              ⚠
            </span>
          ) : (
            <img
              src={iconPreviewSrc(icon.path)}
              alt=''
              loading='lazy'
              decoding='async'
              onError={() => setIsBroken(true)}
            />
          )}
        </span>
        <span className={styles['tile-name']}>{icon.name}</span>
        {isBroken && <span className={styles['tile-note']}>غير موجود</span>}
      </button>

      {onDelete && (
        <button
          type='button'
          className={styles['tile-delete']}
          onClick={() => onDelete(icon)}
          aria-label={`حذف الأيقونة ${icon.name} من المكتبة`}
          title='حذف من المكتبة'
        >
          <span aria-hidden='true'>&times;</span>
        </button>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */

const IconPicker = ({
  value,
  onSelect,
  onClose,
  moduleKey = DEFAULT_MODULE_KEY,
  subModuleKey,
}) => {
  const [draft, setDraft] = useState(value ?? '');
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('all');
  const [custom, setCustom] = useState('');
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null); // { file, previewUrl }
  const [uploadName, setUploadName] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null);

  /* --------------------------------------------------------------- library */

  /**
   * The search term is NOT sent as the backend's `SearchText`. The library is a
   * per-module list of a few dozen rows at most, so filtering it in the browser
   * is instant and — this is the point — keeps it in step with the catalogue
   * half, which can ONLY be filtered client-side. Sending the term would make
   * one half of a single gallery refetch on every keystroke while the other
   * updated immediately.
   */
  const library = useIcons(moduleKey, { subModuleKey });
  const createIcon = useCreateIcon();
  const deleteIcon = useDeleteIcon();

  const libraryIcons = useMemo(
    () =>
      (library.data ?? [])
        .map((row) => ({
          id: row.id,
          name: (row.name || '').trim() || 'بدون اسم',
          path: iconLibraryUrl(row),
        }))
        // A row with no usable URL can be neither previewed nor stored, so it
        // is dropped rather than drawn as a permanently broken tile.
        .filter((icon) => icon.path),
    [library.data]
  );

  /** The object URL outlives the component unless it is handed back. */
  useEffect(
    () => () => {
      if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    },
    [pending]
  );

  /* -------------------------------------------------------------- filtering */

  const isSearching = search.trim().length > 0;
  /** While a term is typed the source filter is ignored — see the toolbar. */
  const activeGroup = isSearching ? 'all' : group;
  const isLibraryVisible = activeGroup === 'all' || activeGroup === LIBRARY_KEY;

  const visibleLibrary = useMemo(() => {
    if (!isLibraryVisible) return [];
    const term = search.trim().toLowerCase();
    if (!term) return libraryIcons;
    return libraryIcons.filter(
      (icon) =>
        icon.name.toLowerCase().includes(term) ||
        icon.path.toLowerCase().includes(term)
    );
  }, [isLibraryVisible, libraryIcons, search]);

  /**
   * Search runs across EVERY group, not just the selected one — an operator
   * looking for "search" should not have to know it lives under `header`. The
   * source select is therefore a filter that the search box overrides.
   */
  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    // `LIBRARY_KEY` matches no catalogue group, so picking the library hides
    // the catalogue with no extra branch.
    const scoped =
      activeGroup === 'all'
        ? GROUPS
        : GROUPS.filter((g) => g.key === activeGroup);
    if (!term) return scoped;

    // Past this point there IS a term, so the group filter is deliberately
    // ignored: a search that silently skipped 53 of 54 groups would look like
    // the icon simply does not exist.
    return GROUPS
      .map((g) => ({
        ...g,
        icons: g.icons.filter(
          (icon) =>
            icon.name.toLowerCase().includes(term) ||
            icon.path.toLowerCase().includes(term)
        ),
      }))
      .filter((g) => g.icons.length > 0);
  }, [activeGroup, search]);

  const catalogueCount = visibleGroups.reduce(
    (total, g) => total + g.icons.length,
    0
  );
  const matchCount = catalogueCount + visibleLibrary.length;
  const totalCount = iconCatalogue.totalIcons + libraryIcons.length;

  /* ---------------------------------------------------------------- upload */

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_BYTES) {
      toast.error(
        `الملف ${formatBytes(file.size)} — الحد الأقصى ${formatBytes(MAX_BYTES)}.`
      );
      event.target.value = '';
      return;
    }

    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending({ file, previewUrl: URL.createObjectURL(file) });
    // Prefilled, not forced: `Name` is mandatory on the API and the file's own
    // name is right far more often than it is wrong. An operator who has
    // already typed a name keeps it.
    setUploadName((current) =>
      current.trim() ? current : fileBaseName(file.name)
    );
  };

  const clearUpload = () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
    setUploadName('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const canUpload =
    Boolean(pending) && uploadName.trim().length > 0 && !createIcon.isLoading;

  const handleUpload = async () => {
    if (!canUpload) return;

    try {
      const created = await createIcon.mutateAsync({
        name: uploadName,
        moduleKey,
        subModuleKey,
        file: pending.file,
        isActive: true,
      });

      clearUpload();

      // Selecting the new icon straight away is the whole reason it was
      // uploaded. If the create response carries no URL the icon is still
      // safely in the library — the invalidated list refetch draws it in the
      // gallery above — so this degrades to "uploaded, now click it" rather
      // than to an error, which is all the old borrowed endpoint could do.
      const url = iconLibraryUrl(created);
      if (url) {
        setDraft(url);
        toast.success('تم رفع الأيقونة واختيارها.');
      } else {
        toast.success('تم رفع الأيقونة وأضيفت إلى المكتبة.');
      }
    } catch (error) {
      toast.error(error?.message || 'تعذّر رفع الأيقونة.');
    }
  };

  /* ---------------------------------------------------------------- delete */

  const handleDelete = async () => {
    if (!confirmTarget || deleteIcon.isLoading) return;

    try {
      await deleteIcon.mutateAsync(confirmTarget.id);
      // The picker's own selection must not keep pointing at a row that no
      // longer exists. The value already SAVED on other menu items is the
      // backend's business, and is what the confirm text warns about.
      if (draft === confirmTarget.path) setDraft('');
      setConfirmTarget(null);
      toast.success('تم حذف الأيقونة من المكتبة.');
    } catch (error) {
      toast.error(error?.message || 'تعذّر حذف الأيقونة.');
    }
  };

  /* ----------------------------------------------------------------- apply */

  const handleConfirm = () => {
    onSelect(draft.trim());
    onClose?.();
  };

  const handleUseCustom = () => {
    const path = custom.trim();
    if (!path) return;
    setDraft(path);
    setCustom('');
  };

  return (
    <Modal
      onClose={onClose}
      closeWhite={false}
      innerContainerClass={styles['picker-modal']}
      modalInnerContainerStyle={{ padding: 0, gap: 0 }}
      closeStyle={{ display: 'none' }}
    >
      <div
        className={styles.dialog}
        dir='rtl'
        role='dialog'
        aria-modal='true'
        aria-labelledby='icon-picker-title'
      >
        <header className={styles['dialog-head']}>
          <div className={styles['dialog-head-text']}>
            <h2 id='icon-picker-title' className={styles['dialog-title']}>
              اختيار أيقونة
            </h2>
            <span className={styles['dialog-count']}>{totalCount} أيقونة</span>
          </div>
          <button
            type='button'
            className={styles['dialog-close']}
            onClick={onClose}
            aria-label='إغلاق'
          >
            <span aria-hidden='true'>&times;</span>
          </button>
        </header>

        {/* ------------------------------------------------------- current */}
        <div className={styles.current}>
          <span className={styles['current-art']}>
            {draft ? (
              <img src={iconPreviewSrc(draft)} alt='' decoding='async' />
            ) : (
              <span className={styles['current-empty']} aria-hidden='true'>
                —
              </span>
            )}
          </span>
          <div className={styles['current-text']}>
            <strong>{draft ? 'الأيقونة المختارة' : 'لا توجد أيقونة'}</strong>
            <code dir='ltr'>{draft || 'سيظهر العنصر بنصه فقط'}</code>
          </div>
          {draft && (
            <button
              type='button'
              className={styles['current-clear']}
              onClick={() => setDraft('')}
            >
              إزالة
            </button>
          )}
        </div>

        {!WEBSITE_URL && (
          <p className={styles['notice-blocked']}>
            عنوان الموقع غير مضبوط، لذلك لن تظهر معاينات أيقونات الموقع. أضف
            <code> REACT_APP_WEBSITE_URL </code>
            إلى ملف <code>.env</code> ثم أعد تشغيل الواجهة. الاختيار يعمل في كل
            الأحوال، وأيقونات المكتبة المرفوعة لا تتأثر لأنها تُحمَّل من الخادم.
          </p>
        )}

        {/* ------------------------------------------------------- toolbar */}
        {/* Search + a native source <select> on one pinned row. The first
            build rendered 54 filter chips in a horizontal rail here — a wall
            of buttons that also depended on inherited flex defaults the
            CMS's global styles do not guarantee. A native select is compact,
            RTL-correct and keyboard-complete for free.

            While a search term is typed the select is pinned to «الكل» and
            disabled: the search deliberately runs across every source (an
            operator looking for `search` should not need to know it lives
            under `header`), and a select that LOOKED active while being
            ignored would be a lie. */}
        <div className={styles.toolbar}>
          <input
            type='search'
            className={styles.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder='ابحث في كل الأيقونات… مثال: search'
            aria-label='بحث في الأيقونات'
          />
          <select
            className={styles['group-select']}
            value={activeGroup}
            disabled={isSearching}
            title={isSearching ? 'البحث يشمل كل المصادر' : undefined}
            onChange={(event) => setGroup(event.target.value)}
            aria-label='مجموعة الأيقونات'
          >
            <option value='all'>كل المجموعات ({totalCount})</option>
            <option value={LIBRARY_KEY}>
              المكتبة المرفوعة ({libraryIcons.length})
            </option>
            {GROUPS.map((g) => (
              <option key={g.key} value={g.key}>
                {g.label} ({g.icons.length})
              </option>
            ))}
          </select>
          <span className={styles['toolbar-count']}>{matchCount} أيقونة</span>
        </div>

        {/* ---------------------------------------------------------- grid */}
        <div className={styles['dialog-body']}>
          {/* The library renders ABOVE the catalogue and carries its own status
              line, because it is the half that can be empty, loading or
              broken — the catalogue is a static import and is none of those. A
              failed list must SAY so: a silently absent section reads as "I
              never uploaded anything".

              The one case where it steps aside is a search that matched
              NOTHING anywhere — the single message below already covers both
              sources, and two "no results" lines stacked on one screen read as
              a layout bug rather than as an answer. */}
          {isLibraryVisible && !(isSearching && matchCount === 0) && (
            <section className={`${styles.group} ${styles['group-library']}`}>
              <h3 className={styles['group-title']}>
                المكتبة المرفوعة
                <code dir='ltr'>{moduleKey}</code>
              </h3>

              {library.isLoading && (
                <p className={styles['group-status']}>جارٍ تحميل المكتبة…</p>
              )}

              {library.isError && (
                <p className={styles['group-error']}>
                  تعذّر تحميل المكتبة:{' '}
                  {library.error?.message || 'خطأ غير معروف'}
                  <button
                    type='button'
                    className={styles['btn-link']}
                    onClick={() => library.refetch()}
                  >
                    إعادة المحاولة
                  </button>
                </p>
              )}

              {!library.isLoading &&
                !library.isError &&
                visibleLibrary.length === 0 && (
                  <p className={styles['group-status']}>
                    {libraryIcons.length === 0
                      ? 'لا توجد أيقونات مرفوعة بعد — ارفع واحدة من الأسفل.'
                      : 'لا توجد أيقونة مرفوعة تطابق البحث.'}
                  </p>
                )}

              {visibleLibrary.length > 0 && (
                <div className={styles.grid}>
                  {visibleLibrary.map((icon) => (
                    <IconTile
                      key={icon.id}
                      icon={icon}
                      isSelected={draft === icon.path}
                      onSelect={setDraft}
                      onDelete={setConfirmTarget}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {matchCount === 0 && isSearching ? (
            <p className={styles.empty}>
              لا توجد أيقونة تطابق «{search}». يمكن لصق رابط الأيقونة بالأسفل.
            </p>
          ) : (
            visibleGroups.map((g) => (
              <section key={g.key} className={styles.group}>
                <h3 className={styles['group-title']}>
                  {g.label}
                  <code dir='ltr'>{g.key}</code>
                </h3>
                <div className={styles.grid}>
                  {g.icons.map((icon) => (
                    <IconTile
                      key={icon.path}
                      icon={icon}
                      isSelected={draft === icon.path}
                      onSelect={setDraft}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>

        {/* -------------------------------------------------------- upload */}
        <div className={styles.tools}>
          <div className={styles.tool}>
            <span className={styles['tool-label']}>
              رفع أيقونة جديدة إلى المكتبة
            </span>
            <div className={styles['tool-row']}>
              <input
                type='text'
                className={styles.input}
                value={uploadName}
                onChange={(event) => setUploadName(event.target.value)}
                placeholder='اسم الأيقونة (مطلوب)'
                aria-label='اسم الأيقونة'
                maxLength={120}
              />
            </div>
            <div className={styles['tool-row']}>
              <input
                ref={fileRef}
                type='file'
                accept={ACCEPT}
                onChange={handleFile}
                className={styles.file}
                aria-label='اختيار ملف الأيقونة'
              />
              {pending && (
                <>
                  <img
                    className={styles['tool-preview']}
                    src={pending.previewUrl}
                    alt=''
                  />
                  <button
                    type='button'
                    className={styles['btn-small']}
                    onClick={handleUpload}
                    disabled={!canUpload}
                    // The button is disabled on an empty name too, so name the
                    // reason rather than leaving a dead control.
                    title={
                      uploadName.trim() ? undefined : 'أدخل اسم الأيقونة أولاً'
                    }
                  >
                    {createIcon.isLoading ? 'جارٍ الرفع…' : 'رفع'}
                  </button>
                  <button
                    type='button'
                    className={styles['btn-quiet-small']}
                    onClick={clearUpload}
                    disabled={createIcon.isLoading}
                  >
                    إلغاء الرفع
                  </button>
                </>
              )}
            </div>
            <span className={styles.hint}>
              SVG أو PNG أو WEBP، بحد أقصى {formatBytes(MAX_BYTES)}. تُضاف
              الأيقونة إلى مكتبة <code dir='ltr'>{moduleKey}</code> مفعّلة
              ومتاحة لإعادة الاستخدام في أي عنصر آخر.
            </span>
          </div>

          <div className={styles.tool}>
            <span className={styles['tool-label']}>لصق رابط الأيقونة</span>
            <div className={styles['tool-row']}>
              <input
                type='text'
                dir='ltr'
                className={`${styles.input} ${styles.ltr}`}
                value={custom}
                onChange={(event) => setCustom(event.target.value)}
                placeholder='الصق رابط الأيقونة هنا'
              />
              <button
                type='button'
                className={styles['btn-small']}
                onClick={handleUseCustom}
                disabled={!custom.trim()}
              >
                استخدام
              </button>
            </div>
            <span className={styles.hint}>
              لرابط خارجي، أو لأيقونة أُضيفت إلى الموقع بعد آخر تحديث لهذه
              المجموعة. لا يُحفظ في المكتبة.
            </span>
          </div>

          {/* Diagnosable, not mysterious: when every tile says «غير موجود»,
              the reason is almost always that the website is not running at
              this address. Print the address. */}
          {WEBSITE_URL && (
            <p className={styles.origin}>
              معاينات أيقونات الموقع تُحمَّل من <code>{WEBSITE_URL}</code> — إن
              ظهرت كمفقودة فتأكد أن الموقع يعمل على هذا العنوان. أيقونات المكتبة
              تُحمَّل من الخادم ولا تتأثر.
            </p>
          )}
        </div>

        <footer className={styles['dialog-foot']}>
          <button
            type='button'
            className={styles['btn-primary']}
            onClick={handleConfirm}
          >
            {draft ? 'استخدام هذه الأيقونة' : 'بدون أيقونة'}
          </button>
          <button type='button' className={styles['btn-secondary']} onClick={onClose}>
            إلغاء
          </button>
        </footer>
      </div>

      {/* A portal on top of this one. The Modal atom portals to <body>, so the
          later mount paints above — no z-index juggling. */}
      {confirmTarget && (
        <ConfirmDialog
          title='حذف الأيقونة من المكتبة'
          lines={[
            `سيتم حذف «${confirmTarget.name}» نهائياً من مكتبة الأيقونات.`,
            {
              text: 'أي عنصر محفوظ يستخدم هذه الأيقونة سيفقد صورته في الموقع.',
              strong: true,
            },
            'لا يمكن التراجع عن هذا الإجراء.',
          ]}
          confirmLabel='حذف'
          isBusy={deleteIcon.isLoading}
          onConfirm={handleDelete}
          onClose={() => setConfirmTarget(null)}
        />
      )}
    </Modal>
  );
};

export default IconPicker;
